'use client';
import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

const STABLE_FRAMES = 35;
const MAX_WAIT_MS = 5000;

const scrollWhenStable = (hash: string): (() => void) => {
  const start = Date.now();
  let lastHeight = -1;
  let stableCount = 0;
  let rafId = 0;

  const check = () => {
    const height = document.documentElement.scrollHeight;
    if (lastHeight === -1) {
      // Frame 0: capture baseline at a frame boundary, then start comparing
      lastHeight = height;
      rafId = requestAnimationFrame(check);
      return;
    }
    if (height !== lastHeight) {
      stableCount = 0;
      lastHeight = height;
    } else {
      stableCount++;
    }

    if (stableCount >= STABLE_FRAMES || Date.now() - start > MAX_WAIT_MS) {
      const el = document.querySelector(hash);
      if (el) {
        const stickyHeader = document.querySelector<HTMLElement>('header[data-testid="overlay-header"]');
        const offset = stickyHeader?.offsetHeight ?? 0;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'instant' });
      }
    } else {
      rafId = requestAnimationFrame(check);
    }
  };

  rafId = requestAnimationFrame(check);
  return () => cancelAnimationFrame(rafId);
};

const HashScroll = () => {
  const pathname = usePathname();

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    if (document.readyState === 'complete') {
      return scrollWhenStable(hash);
    } else {
      const onLoad = () => scrollWhenStable(hash);
      window.addEventListener('load', onLoad, { once: true });
      return () => window.removeEventListener('load', onLoad);
    }
  }, [pathname]);

  return null;
};

export default HashScroll;
